import {
    createContext,
    ReactNode,
    useContext,
    useEffect,
    useMemo,
    useState,
} from "react";
import {createPortal} from "react-dom";

interface TopBarContextValue {
    centerEl: HTMLElement | null;
    rightEl: HTMLElement | null;
    setCenterEl: (el: HTMLElement | null) => void;
    setRightEl: (el: HTMLElement | null) => void;
}

const TopBarContext = createContext<TopBarContextValue>({
    centerEl: null,
    rightEl: null,
    setCenterEl: () => {},
    setRightEl: () => {},
});

export const TopBarProvider = ({children}: {children: ReactNode}) => {
    const [centerEl, setCenterEl] = useState<HTMLElement | null>(null);
    const [rightEl, setRightEl] = useState<HTMLElement | null>(null);

    const value = useMemo(
        () => ({centerEl, rightEl, setCenterEl, setRightEl}),
        [centerEl, rightEl]
    );

    return (
        <TopBarContext.Provider value={value}>
            {children}
        </TopBarContext.Provider>
    );
};

export const useTopBarSlots = () => useContext(TopBarContext);

interface TopBarSlotProps {
    slot: "center" | "right";
    children: ReactNode;
}

export const TopBarSlot = ({slot, children}: TopBarSlotProps) => {
    const {centerEl, rightEl} = useContext(TopBarContext);
    const target = slot === "center" ? centerEl : rightEl;
    const [ready, setReady] = useState(target !== null);

    useEffect(() => {
        setReady(target !== null);
    }, [target]);

    if (!ready || !target) return null;
    return createPortal(children, target);
};
